import { useEffect, useRef, useState } from 'react';
import type { SimilarCategorySuggestion } from '@/types';
import { categoryBudgetApi } from '@/lib/api';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';

export interface UseSimilarCategorySuggestionArgs {
  /** Gate: open create-mode form, not mid-save. False clears any pending/inflight work. */
  enabled: boolean;
  /** The category name as typed; the match fires after it settles. */
  name: string;
  /**
   * Unlike useCategorySuggestion, this fires on every settle — including a 204 (no
   * match) — so a stale chip for a name the user has since edited past gets cleared.
   */
  onSuggestion: (suggestion: SimilarCategorySuggestion | null) => void;
}

const DEBOUNCE_MS = 600;
const MIN_NAME_LENGTH = 3;

/**
 * Debounced "did you mean this existing category?" duplicate-avoidance nudge.
 * Failure of any kind is a silent no-op (the endpoint answers 204 when it has
 * nothing) — the form just behaves as if the feature didn't exist.
 */
export function useSimilarCategorySuggestion({
  enabled,
  name,
  onSuggestion,
}: UseSimilarCategorySuggestionArgs): { loading: boolean } {
  const authedFetch = useAuthedFetcher();
  const [loading, setLoading] = useState(false);

  const onSuggestionRef = useRef(onSuggestion);
  useEffect(() => {
    onSuggestionRef.current = onSuggestion;
  });
  const controllerRef = useRef<AbortController | null>(null);

  const trimmed = name.trim();

  useEffect(() => {
    if (!enabled || trimmed.length < MIN_NAME_LENGTH) {
      controllerRef.current?.abort();
      controllerRef.current = null;
      setLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setLoading(true);

      authedFetch((token) =>
        categoryBudgetApi.suggestSimilarCategory(token, { name: trimmed }, controller.signal),
      )
        .then((suggestion) => {
          // A response that lost the race to a newer request (or to close) is stale.
          if (controller.signal.aborted) return;
          onSuggestionRef.current(suggestion ?? null);
        })
        .catch(() => {
          // Silent by contract: abort, network, auth — the form works without AI.
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controllerRef.current?.abort();
      controllerRef.current = null;
      setLoading(false);
    };
  }, [enabled, trimmed, authedFetch]);

  return { loading };
}
